import type { ProcessoArbitralStatus } from '@/features/secretaria-arbitral/types';
import { isRecord } from '@/shared/utils/apiResponse';

import type {
  BlocoLocal,
  EtapaLocal,
  FluxoArbitralEditavel,
  MotivoSomenteEditorDeNos,
  ParametroTrocado,
  RamoPasso,
  RamoPath,
  RegraCondicao,
  TipoBloco,
  TipoBlocoComNo,
} from '../types/editor';
import type { FluxoArbitralStatus, GatilhoFluxo, ModoAutoriaFluxo, RitoFluxo } from '../types';
import {
  compilarAlvoDaIntimacao,
  ehParteUnicaDoCompilador,
} from '../catalogo/destinatarioDaIntimacao';
import { compilarEmailsCitacao, lerEmailsCitacao } from '../catalogo/emailsCitacao';
import { avisaEAguarda, lerAvisoAtraso } from '../catalogo/esperaVencimento';
import {
  pendenciaIdDoNode,
  pendenciaVigiadaDe,
  variavelGravadaPelaPendencia,
} from '../catalogo/pendenciaVigiada';
import { prazoVigiadoDe, variavelGravadaPeloPrazo } from '../catalogo/prazoVigiado';
import { passoDoRamo, ramosDoBloco } from '../catalogo/ramos';

import { CHAVES_FORA_DOS_PARAMETROS, parametrosDoNode } from './parametrosDoNode';

interface RawNode {
  id: string;
  type?: string;
  position?: { x: number; y: number };
  data?: Record<string, unknown>;
}

interface RawConnection {
  id: string;
  sourceNodeId: string;
  targetNodeId: string;
  sourceHandle?: string;
  targetHandle?: string;
  label?: string;
}

interface RawDefinition {
  nodes?: RawNode[];
  connections?: RawConnection[];
  variables?: unknown[];
  metadata?: Record<string, unknown>;
}

interface RawMetadataEtapa {
  id: string;
  nome: string;
  ordem: number;
  statusEntrada: ProcessoArbitralStatus | null;
  statusSaida: ProcessoArbitralStatus | null;
}

export interface RawFlowResponse {
  id: string;
  name: string;
  description?: string | null;
  status: FluxoArbitralStatus;
  scenarioType?: string | null;
  startNodeId?: string | null;
  version?: number;
  definition?: RawDefinition | null;
  metadata?: Record<string, unknown> | null;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Tipo de bloco do editor → `node.type` reconhecido pelo dispatch map do
 * backend (`arbitral/flows/nodeExecutors/index.ts` + core `condition`).
 */
export const NODE_TYPE_POR_TIPO: Record<TipoBlocoComNo, string> = {
  CONDICAO: 'condition',
  MUDAR_STATUS: 'arbitral.set_status',
  SET_SITUACAO: 'arbitral.set_situacao',
  CRIAR_ANDAMENTO: 'arbitral.criar_andamento',
  CRIAR_PENDENCIA: 'arbitral.criar_pendencia',
  GERAR_DOCUMENTO: 'arbitral.gerar_certidao',
  PUBLICAR_DECURSO: 'arbitral.publicar_decurso',
  PUBLICAR_TRANSITO: 'arbitral.publicar_transito',
  FINALIZAR_PROCESSO: 'arbitral.finalizar_processo',
  AGUARDAR_EMBARGOS: 'arbitral.aguardar_prazo_embargos',
  SORTEAR_ARBITRO: 'arbitral.sortear_arbitro',
  EXIGIR_TERMO_IMPARCIALIDADE: 'arbitral.exigir_termo_imparcialidade',
  CONCLUSO: 'arbitral.concluso',
  INTIMAR_SENTENCA: 'arbitral.intimar_sentenca',
  CITACAO: 'arbitral.citar',
  COBRAR_ASAAS: 'arbitral.cobrar_pagamento',
  REGISTRAR_CONSUMO_CONVENIO: 'arbitral.registrar_consumo_convenio',
  ENVIAR_EMAIL: 'arbitral.send_comunicacao_email',
  ENVIAR_WHATSAPP: 'arbitral.send_comunicacao_whatsapp',
  NOTIF_INAPP: 'arbitral.notif_inapp',
  ENVIAR_AR: 'arbitral.send_carta',
  INICIAR_PRAZO: 'arbitral.iniciar_prazo',
  AGUARDAR_EVENTO: 'arbitral.aguardar_evento',
  AGUARDAR_VISUALIZACAO: 'arbitral.aguardar_visualizacao',
  AGUARDAR_CITACAO: 'arbitral.aguardar_citacao',
  DETECTAR_MANIFESTACAO: 'arbitral.detectar_manifestacao',
};

const TIPOS_BLOCO_VALIDOS: ReadonlySet<TipoBlocoComNo> = new Set<TipoBlocoComNo>(
  Object.keys(NODE_TYPE_POR_TIPO) as TipoBlocoComNo[],
);

/**
 * Blocos cujo `node.type` ainda não tem executor no backend. Mantidos no
 * catálogo (UX), mas a validação bloqueia a publicação até existir executor.
 */
export const BLOCOS_SEM_EXECUTOR: ReadonlySet<TipoBloco> = new Set<TipoBloco>();

/**
 * Blocos cujo mapeamento de parâmetros (front → `node.data` do backend) está
 * verificado contra a interface `*NodeData` do executor. Só estes podem ser
 * publicados. Os demais têm executor, mas a UI ainda não captura todos os
 * campos exigidos (ex.: `tipo: TipoAndamento`, `parteArbitralId`) — o
 * mapeamento completo é trabalho de fase seguinte.
 */
export const BLOCOS_PUBLICAVEIS: ReadonlySet<TipoBloco> = new Set<TipoBloco>([
  'CONDICAO',
  'MUDAR_STATUS',
  'SET_SITUACAO',
  'CRIAR_ANDAMENTO',
  'SORTEAR_ARBITRO',
  'EXIGIR_TERMO_IMPARCIALIDADE',
  'CONCLUSO',
  'AGUARDAR_EVENTO',
  'AGUARDAR_VISUALIZACAO',
  'AGUARDAR_CITACAO',
  'INTIMAR_SENTENCA',
  'AGUARDAR_EMBARGOS',
  'INICIAR_PRAZO',
  'CRIAR_PENDENCIA',
  'GERAR_DOCUMENTO',
  'PUBLICAR_DECURSO',
  'FINALIZAR_PROCESSO',
  'PUBLICAR_TRANSITO',
  'COBRAR_ASAAS',
  'ENVIAR_EMAIL',
  'ENVIAR_WHATSAPP',
  'NOTIF_INAPP',
  'ENVIAR_AR',
  'CITACAO',
  'REGISTRAR_CONSUMO_CONVENIO',
  // Não vira nó: publica como a continuação do bloco anterior (`alvoDoSalto`).
  'IR_PARA',
]);

/**
 * Deriva o conjunto de `TipoBloco` SEM executor a partir das capabilities do
 * backend (node types com executor registrado), cruzando com `NODE_TYPE_POR_TIPO`.
 * Fonte única front↔back (Tela 16 Frente C/C2). Quando as capabilities ainda
 * não foram carregadas, cai no `BLOCOS_SEM_EXECUTOR` estático (fallback).
 */
export function blocosSemExecutorDe(
  capabilities: readonly string[] | null,
): ReadonlySet<TipoBloco> {
  if (!capabilities || capabilities.length === 0) return BLOCOS_SEM_EXECUTOR;
  const registrados = new Set(capabilities);
  const semExecutor = new Set<TipoBloco>();
  for (const [tipo, nodeType] of Object.entries(NODE_TYPE_POR_TIPO) as [TipoBlocoComNo, string][]) {
    if (!registrados.has(nodeType)) semExecutor.add(tipo);
  }
  return semExecutor;
}

/**
 * Tipo de bloco que vira nó. O "Ir para" não está aqui: sem `editorSource` (carga pelos nós) ele
 * não existe no grafo, e as ligações que ele produziu voltam como a continuação dos blocos.
 */
function isTipoBlocoComNo(value: unknown): value is TipoBlocoComNo {
  return typeof value === 'string' && TIPOS_BLOCO_VALIDOS.has(value as TipoBlocoComNo);
}

function extractRito(raw: RawFlowResponse): RitoFluxo | null {
  const meta = raw.metadata ?? raw.definition?.metadata ?? null;
  if (meta && typeof meta === 'object') {
    const rito = (meta as Record<string, unknown>).rito;
    if (
      rito === 'SUMARIO' ||
      rito === 'SUMARISSIMO' ||
      rito === 'ORDINARIO' ||
      rito === 'ARBITRIO'
    ) {
      return rito;
    }
  }
  return null;
}

function extractGatilho(raw: RawFlowResponse): GatilhoFluxo | null {
  const meta = raw.metadata ?? raw.definition?.metadata ?? null;
  if (!meta || typeof meta !== 'object') return null;
  const candidate = (meta as Record<string, unknown>).gatilho;
  if (candidate && typeof candidate === 'object') {
    const eventType = (candidate as Record<string, unknown>).eventType;
    if (typeof eventType === 'string' && eventType.length > 0) {
      return { tipo: 'EVENTO', eventType };
    }
  }
  return null;
}

function extractModoAutoria(raw: RawFlowResponse): ModoAutoriaFluxo {
  const meta = raw.definition?.metadata ?? raw.metadata ?? null;
  if (!meta || typeof meta !== 'object') return 'etapas';
  return (meta as Record<string, unknown>).modoAutoria === 'conversa' ? 'conversa' : 'etapas';
}

function parseStatus(value: unknown): ProcessoArbitralStatus | null {
  if (typeof value !== 'string' || value.length === 0) return null;
  return value as ProcessoArbitralStatus;
}

function etapasFromSource(source: unknown): EtapaLocal[] | null {
  if (!source || typeof source !== 'object') return null;
  const etapas = (source as Record<string, unknown>).etapas;
  if (!Array.isArray(etapas)) return null;
  return etapas as EtapaLocal[];
}

function etapasFromNodes(definition: RawDefinition): EtapaLocal[] {
  const meta = (definition.metadata ?? {}) as Record<string, unknown>;
  const rawEtapasMeta = (meta.etapas as unknown) ?? [];
  const etapasMeta: RawMetadataEtapa[] = Array.isArray(rawEtapasMeta)
    ? (rawEtapasMeta as RawMetadataEtapa[])
    : [];

  const etapasMap = new Map<string, EtapaLocal>();
  for (const m of etapasMeta) {
    etapasMap.set(m.id, {
      id: m.id,
      nome: m.nome ?? 'Etapa',
      ordem: m.ordem ?? 0,
      statusEntrada: parseStatus(m.statusEntrada),
      statusSaida: parseStatus(m.statusSaida),
      blocos: [],
    });
  }

  const nodes = definition.nodes ?? [];
  for (const node of nodes) {
    const data: Record<string, unknown> = node.data ?? {};
    const tipoRaw = data.tipoBloco ?? data.tipo;
    if (!isTipoBlocoComNo(tipoRaw)) continue;
    const etapaId = typeof data.etapaId === 'string' ? data.etapaId : null;
    if (!etapaId) continue;
    let etapa = etapasMap.get(etapaId);
    if (!etapa) {
      etapa = {
        id: etapaId,
        nome: (typeof data.etapaNome === 'string' ? data.etapaNome : 'Etapa') as string,
        ordem: (typeof data.etapaOrdem === 'number' ? data.etapaOrdem : etapasMap.size) as number,
        statusEntrada: null,
        statusSaida: null,
        blocos: [],
      };
      etapasMap.set(etapaId, etapa);
    }
    etapa.blocos.push({
      id: node.id,
      tipo: tipoRaw,
      label: typeof data.label === 'string' ? data.label : undefined,
      ordem: typeof data.ordem === 'number' ? data.ordem : etapa.blocos.length,
      parametros: parametrosDoNode(tipoRaw, data),
    });
  }

  return Array.from(etapasMap.values());
}

/**
 * Copia só os campos que `BlocoLocal` declara hoje.
 *
 * `etapasFromSource` devolve o `editorSource` com um `as EtapaLocal[]` e os
 * reducers propagam os blocos por spread, então uma chave gravada por uma
 * versão antiga do editor sobreviveria no documento para sempre — e
 * `toFlowDefinitionPatch` a regravaria em `metadata.editorSource`. Foi o caso
 * do `modo: 'aguarda_confirmacao'`: republicar pelo editor o reconverteria em
 * `node.config.executionMode`, que congela o fluxo.
 */
function saneiaBloco(bloco: BlocoLocal, ordem: number): BlocoLocal {
  return {
    id: bloco.id,
    tipo: bloco.tipo,
    label: bloco.label,
    ordem,
    parametros: bloco.parametros,
  };
}

function normalizarEtapas(etapas: EtapaLocal[]): EtapaLocal[] {
  return etapas
    .slice()
    .sort((a, b) => a.ordem - b.ordem)
    .map((e, idx) => ({
      ...e,
      ordem: idx,
      blocos: e.blocos
        .slice()
        .sort((a, b) => a.ordem - b.ordem)
        .map(saneiaBloco),
    }));
}

export function fromFlowResponse(raw: RawFlowResponse): FluxoArbitralEditavel {
  const definition = raw.definition ?? {};
  const meta = (definition.metadata ?? raw.metadata ?? {}) as Record<string, unknown>;

  const etapasSource = etapasFromSource(meta.editorSource);
  const etapas = normalizarEtapas(etapasSource ?? etapasFromNodes(definition));

  return {
    id: raw.id,
    name: raw.name,
    description: raw.description ?? null,
    status: raw.status,
    rito: extractRito(raw),
    gatilho: extractGatilho(raw),
    modoAutoria: extractModoAutoria(raw),
    version: raw.version ?? 1,
    etapas,
    startNodeId: raw.startNodeId ?? null,
    motivoSomenteEditorDeNos: motivoSomenteEditorDeNos(definition, raw.startNodeId, etapas),
  };
}

/**
 * Compara o fluxo carregado com o que um save pela régua gravaria no lugar dele. Nó a menos
 * apaga bloco; ligação ou início diferente muda o caminho sem apagar nada: é o ramo que
 * `etapasFromNodes` achata em sequência, ou o `editorSource` que ficou para trás dos nós; e
 * parâmetro diferente muda o que o nó executa sem mudar o caminho: é o valor do `node.data` que
 * `parametrosDoNode` não consegue devolver à tela, e o compilador troca pelo default.
 */
function motivoSomenteEditorDeNos(
  definition: RawDefinition,
  startNodeId: string | null | undefined,
  etapas: EtapaLocal[],
): MotivoSomenteEditorDeNos | null {
  const nos = definition.nodes ?? [];
  if (nos.length === 0) return null;
  const regua = compilarEtapas(etapas);

  const recompilados = new Set(regua.nodes.map((no) => no.id));
  const apagados = nos.filter((no) => !recompilados.has(no.id)).length;
  if (apagados > 0) return { tipo: 'APAGARIA_BLOCOS', quantidade: apagados };

  const salvas = ligacoesDoGrafo(nos, definition.connections ?? []);
  const recompiladas = ligacoesDoGrafo(regua.nodes, regua.connections);
  const ligacoesSoNoSalvo = [...salvas].filter((ligacao) => !recompiladas.has(ligacao)).sort();
  const ligacoesSoNaRegua = [...recompiladas].filter((ligacao) => !salvas.has(ligacao)).sort();
  const inicioMuda = inicioDeclaradoMuda(startNodeId, nos, regua.startNodeId);

  if (ligacoesSoNoSalvo.length > 0 || ligacoesSoNaRegua.length > 0 || inicioMuda) {
    return { tipo: 'MUDARIA_CAMINHO', ligacoesSoNoSalvo, ligacoesSoNaRegua, inicioMuda };
  }

  const parametros = parametrosQueOSaveTrocaria(nos, regua.nodes);
  return parametros.length > 0 ? { tipo: 'TROCARIA_PARAMETROS', parametros } : null;
}

// O roteamento do `node.data` é comparado como ligação (`roteamentoNoData`), antes desta etapa.
const CHAVES_DE_ROTEAMENTO: ReadonlySet<string> = new Set([
  'timeoutNodeId',
  'fallbackNodeId',
  'eventRouting',
]);

// O formato antigo que o compilador troca de propósito pelo atual, por tipo de nó: a mudança
// é a migração desenhada, não perda. Em regra casa chave E valor: o mesmo campo com um valor que
// o compilador nunca emitiu não tem migração desenhada e é comparado como os outros. O prazo do
// termo sai com qualquer valor porque o core ignora qualquer valor (core#2456, web#1407): o prazo
// vem do cadastro "Aceite Nomeação e Termo (Árbitro)". A `visibilidade` do andamento sai com
// qualquer valor porque o core a ignora (C21: não existe andamento interno, todo andamento nasce
// PUBLICO); sem esta entrada, todo "Registrar andamento" salvo com a chave travaria o save.
const CHAVES_QUE_O_SAVE_MIGRA: ReadonlyMap<string, (chave: string, valor: unknown) => boolean> =
  new Map([
    [NODE_TYPE_POR_TIPO.INTIMAR_SENTENCA, ehParteUnicaDoCompilador],
    [NODE_TYPE_POR_TIPO.EXIGIR_TERMO_IMPARCIALIDADE, (chave) => chave === 'prazoDiasUteis'],
    [NODE_TYPE_POR_TIPO.CRIAR_ANDAMENTO, (chave) => chave === 'visibilidade'],
  ]);

// O que o core faz com a chave ausente, onde difere do default que `construirDataNode` emite:
// `gerarDocumentoPdf`/`somenteSeEmailNaoAberto` ausentes valem `false` nos executores. O nó
// salvo sem a chave é comparado como se a trouxesse com esse valor.
const DEFAULT_DO_CORE_QUANDO_AUSENTE: ReadonlyMap<
  string,
  Readonly<Record<string, unknown>>
> = new Map([
  [NODE_TYPE_POR_TIPO.INTIMAR_SENTENCA, { gerarDocumentoPdf: false }],
  [NODE_TYPE_POR_TIPO.ENVIAR_AR, { somenteSeEmailNaoAberto: false }],
]);

/**
 * Cada chave do `node.data` salvo que o save pela régua regravaria com outro valor. Vale para
 * todo tipo de bloco e todo campo, não só para os que se sabe que não voltam (o `visivelPara`
 * com mais de um papel, o `mode` da condição): um campo novo no executor, gravado por migration
 * ou pelo editor de fluxos, também trava o save em vez de sumir.
 *
 * Só conta a chave que o salvo traz. A que só o compilador emite é o default explícito do que o
 * executor já faz com a chave ausente (`respectBusinessHours`, `modoTexto`...), e sem ela todo
 * fluxo gravado antes do default existir travaria. Onde o core usa outro valor para a chave
 * ausente, o salvo é lido com ele (`DEFAULT_DO_CORE_QUANDO_AUSENTE`). Ficam de fora também a
 * autoria (`CHAVES_FORA_DOS_PARAMETROS`), o roteamento, o `tipo` que marca o bloco salvo sem
 * `tipoBloco` e o formato antigo que o compilador migra (`CHAVES_QUE_O_SAVE_MIGRA`).
 */
function parametrosQueOSaveTrocaria(salvos: RawNode[], regravados: RawNode[]): ParametroTrocado[] {
  const regravadoPorId = new Map(regravados.map((no) => [no.id, no.data ?? {}]));
  return salvos.flatMap((no) => {
    const regravado = regravadoPorId.get(no.id);
    if (!regravado) return [];
    const migrada = CHAVES_QUE_O_SAVE_MIGRA.get(no.type ?? '');
    return Object.entries(dataComoOCoreExecuta(no))
      .filter(([chave, valor]) => ehParametroDeExecucao(chave, valor))
      .filter(([chave, valor]) => !migrada?.(chave, valor))
      .filter(([chave, valor]) => !mesmoValorJson(valor, regravado[chave]))
      .map(([chave]) => ({ noId: no.id, chave }));
  });
}

function dataComoOCoreExecuta(no: RawNode): Record<string, unknown> {
  const presentes = Object.entries(no.data ?? {}).filter(([, valor]) => valor !== undefined);
  return {
    ...DEFAULT_DO_CORE_QUANDO_AUSENTE.get(no.type ?? ''),
    ...Object.fromEntries(presentes),
  };
}

function ehParametroDeExecucao(chave: string, valor: unknown): boolean {
  if (CHAVES_FORA_DOS_PARAMETROS.has(chave) || CHAVES_DE_ROTEAMENTO.has(chave)) return false;
  return !(chave === 'tipo' && isTipoBlocoComNo(valor));
}

/** Igualdade de valor JSON; chave com `undefined` conta como ausente, como o core a guarda. */
function mesmoValorJson(salvo: unknown, regravado: unknown): boolean {
  if (salvo === regravado) return true;
  if (Array.isArray(salvo) || Array.isArray(regravado)) {
    return (
      Array.isArray(salvo) &&
      Array.isArray(regravado) &&
      salvo.length === regravado.length &&
      salvo.every((item, i) => mesmoValorJson(item, regravado[i]))
    );
  }
  if (!isRecord(salvo) || !isRecord(regravado)) return false;
  const chaves = new Set([...Object.keys(salvo), ...Object.keys(regravado)]);
  return [...chaves].every((chave) => mesmoValorJson(salvo[chave], regravado[chave]));
}

/**
 * O caminho de cada nó, como o motor do core o lê:
 * - a saída só distingue caminho na condição (`true`/`false`, aceitos também em `label`, como em
 *   `conditionRouting.ts` do core); nos demais nós o motor segue a primeira ligação, então o
 *   `sourceHandle` (`output`, que o editor de fluxos grava) não muda nada e fica de fora;
 * - o roteamento que mora no `node.data` (vencimento da espera, decisão por evento e o
 *   `fallbackNodeId` da condição) também muda para onde a execução vai, então entra como ligação;
 * - o `id` da ligação fica de fora: mudou de formato entre versões do compilador sem mudar o caminho;
 * - a saída de bloco terminal fica de fora: é a aresta que o compilador anterior ao #902 emitia e
 *   que um save pela régua remove de propósito, porque reconvergia o processo encerrado no
 *   caminho feliz.
 */
function ligacoesDoGrafo(nos: RawNode[], connections: RawConnection[]): Set<string> {
  return new Set(
    arestasDoGrafo(nos, connections).map(({ origem, destino, saida }) =>
      chaveDaLigacao(origem, destino, saida),
    ),
  );
}

interface ArestaDoGrafo {
  origem: string;
  destino: string;
  saida: string | null;
}

function arestasDoGrafo(nos: RawNode[], connections: RawConnection[]): ArestaDoGrafo[] {
  const tipoPorId = new Map(nos.map((no) => [no.id, no.type]));
  const arestas: ArestaDoGrafo[] = [];
  for (const ligacao of connections) {
    const tipoDaOrigem = tipoPorId.get(ligacao.sourceNodeId);
    if (tipoDaOrigem && NODE_TYPES_TERMINAIS.has(tipoDaOrigem)) continue;
    const saida = tipoDaOrigem === 'condition' ? saidaDaCondicao(ligacao) : null;
    arestas.push({ origem: ligacao.sourceNodeId, destino: ligacao.targetNodeId, saida });
  }
  for (const no of nos) {
    for (const [destino, saida] of roteamentoNoData(no.data ?? {})) {
      arestas.push({ origem: no.id, destino, saida });
    }
  }
  return arestas;
}

function saidaDaCondicao(ligacao: RawConnection): string | null {
  for (const saida of ['true', 'false']) {
    if (ligacao.sourceHandle === saida || ligacao.label === saida) return saida;
  }
  return ligacao.sourceHandle || null;
}

function roteamentoNoData(data: Record<string, unknown>): Array<[destino: string, saida: string]> {
  const rotas: Array<[string, string]> = [];
  if (typeof data.timeoutNodeId === 'string' && data.timeoutNodeId) {
    rotas.push([data.timeoutNodeId, '@vencimento']);
  }
  if (typeof data.fallbackNodeId === 'string' && data.fallbackNodeId) {
    rotas.push([data.fallbackNodeId, '@fallback']);
  }
  if (data.eventRouting && typeof data.eventRouting === 'object') {
    for (const [evento, destino] of Object.entries(data.eventRouting)) {
      if (typeof destino === 'string' && destino) rotas.push([destino, `@evento:${evento}`]);
    }
  }
  return rotas;
}

function chaveDaLigacao(origem: string, destino: string, saida: string | null): string {
  return saida ? `${origem} -> ${destino} [${saida}]` : `${origem} -> ${destino}`;
}

/**
 * Só conta o início que o fluxo declara e que existe entre os nós: sem ele o core escolhe o
 * início pelo gatilho, e o salvo não diz qual caminho a régua estaria trocando.
 */
function inicioDeclaradoMuda(
  startNodeId: string | null | undefined,
  nos: RawNode[],
  inicioDaRegua: string | null,
): boolean {
  if (!startNodeId || !nos.some((no) => no.id === startNodeId)) return false;
  return startNodeId !== inicioDaRegua;
}

export interface FlowDefinitionPatch {
  definition: RawDefinition;
  metadata: Record<string, unknown>;
  startNodeId: string | null;
}

interface CompilerCtx {
  nodes: RawNode[];
  connections: RawConnection[];
  posY: { value: number };
  /** Etapa sendo compilada — injetada em cada node.data (autoria, Tela 1/18). */
  etapaAtual?: { id: string; nome: string; ordem: number };
  /**
   * Blocos que viram nó neste fluxo. Um "Ir para" só emenda num destino daqui; fora disso
   * (sem destino, destino removido, destino que é outro "Ir para" ou que mora num ramo que o
   * compilador não emite) ele cai na continuação natural e não deixa alvo órfão no grafo.
   */
  idsComNo: ReadonlySet<string>;
  /**
   * Só na análise do grafo (`grafoDeExecucao`): cada "Ir para" válido entra como um ponto de
   * passagem `salto.id → destino`, para a validação saber por qual salto um laço se fecha. Na
   * compilação para o core fica ausente e o salto é só a troca de alvo.
   */
  saltos?: Map<string, string>;
}

type BlocoComNo = BlocoLocal & { tipo: TipoBlocoComNo };

function temNo(bloco: BlocoLocal): bloco is BlocoComNo {
  return bloco.tipo !== 'IR_PARA';
}

/**
 * Para onde a execução segue ao chegar no "Ir para": o destino, quando ele vira um nó do fluxo;
 * senão `null` e quem chama usa a continuação natural.
 */
function alvoDoSalto(salto: BlocoLocal, ctx: CompilerCtx): string | null {
  const destino = salto.parametros.destinoBlocoId;
  if (typeof destino !== 'string' || !ctx.idsComNo.has(destino)) return null;
  if (!ctx.saltos) return destino;
  ctx.saltos.set(salto.id, destino);
  return salto.id;
}

function lerSubBlocos(bloco: BlocoLocal, chave: string): BlocoLocal[] {
  const valor = bloco.parametros[chave];
  return Array.isArray(valor) ? (valor as BlocoLocal[]) : [];
}

/** Lista de blocos de um ramo do bloco — resolve a sub-chave de ramos por evento. */
export function blocosDoRamo(bloco: BlocoLocal, passo: Omit<RamoPasso, 'condId'>): BlocoLocal[] {
  const valor = bloco.parametros[passo.ramo];
  if (passo.evento === undefined) return Array.isArray(valor) ? (valor as BlocoLocal[]) : [];
  if (!valor || typeof valor !== 'object' || Array.isArray(valor)) return [];
  const lista = (valor as Record<string, unknown>)[passo.evento];
  return Array.isArray(lista) ? (lista as BlocoLocal[]) : [];
}

/** Lista de blocos no fim de um caminho de ramos, partindo dos blocos da etapa. */
export function blocosNoRamoPath(blocos: BlocoLocal[], path: RamoPath): BlocoLocal[] {
  let atuais = blocos;
  for (const passo of path) {
    const container = atuais.find((b) => b.id === passo.condId);
    if (!container) return [];
    atuais = blocosDoRamo(container, passo);
  }
  return atuais;
}

/** Bloco que carrega o ÚLTIMO ramo do caminho (dono do ramo endereçado). */
export function encontrarCaminhoDoBloco(
  blocos: BlocoLocal[],
  blocoId: string,
  pathAtual: RamoPath = [],
): { path: RamoPath; bloco: BlocoLocal } | null {
  for (const bloco of blocos) {
    if (bloco.id === blocoId) return { path: pathAtual, bloco };

    for (const ramo of ramosDoBloco(bloco)) {
      const passo = passoDoRamo(ramo);
      const achado = encontrarCaminhoDoBloco(blocosDoRamo(bloco, passo), blocoId, [
        ...pathAtual,
        { condId: bloco.id, ...passo },
      ]);
      if (achado) return achado;
    }
  }
  return null;
}

export function blocoContainerNoPath(blocos: BlocoLocal[], path: RamoPath): BlocoLocal | null {
  let atuais = blocos;
  let container: BlocoLocal | null = null;
  for (const passo of path) {
    container = atuais.find((b) => b.id === passo.condId) ?? null;
    if (!container) return null;
    atuais = blocosDoRamo(container, passo);
  }
  return container;
}

/**
 * Substitui imutavelmente a lista de blocos no fim do caminho, devolvendo a
 * lista top-level da etapa reconstruída (para o dispatch de `atualizarBloco`
 * patchar o bloco raiz `path[0].condId`).
 */
export function atualizarRamoNoPath(
  blocos: BlocoLocal[],
  path: RamoPath,
  novos: BlocoLocal[],
): BlocoLocal[] {
  if (path.length === 0) return novos;
  const [passo, ...resto] = path;
  return blocos.map((bloco) => {
    if (bloco.id !== passo.condId) return bloco;
    const filhos = atualizarRamoNoPath(blocosDoRamo(bloco, passo), resto, novos);
    const valorAtual = bloco.parametros[passo.ramo];
    const valorRamo =
      passo.evento === undefined
        ? filhos
        : {
            ...(valorAtual && typeof valorAtual === 'object' && !Array.isArray(valorAtual)
              ? (valorAtual as Record<string, unknown>)
              : {}),
            [passo.evento]: filhos,
          };
    return { ...bloco, parametros: { ...bloco.parametros, [passo.ramo]: valorRamo } };
  });
}

function construirDataCondicao(parametros: Record<string, unknown>): Record<string, unknown> {
  const regras = Array.isArray(parametros.regras) ? (parametros.regras as RegraCondicao[]) : [];
  const conector = parametros.conector === 'OR' ? 'OR' : 'AND';
  return {
    mode: 'simple',
    logicOperator: conector,
    conditions: regras.map((r) => ({
      id: r.id,
      variable: r.campo,
      operator: r.operador,
      value: r.valor,
    })),
  };
}

/**
 * Só emite o vínculo quando o core o lê (A-9): a espera precisa vigiar "Prazo vencido" e o prazo
 * precisa estar solto. Sem os campos, o `node.data` sai idêntico ao de antes — fluxo publicado
 * não muda de comportamento.
 */
function prazoIdDaEspera(p: BlocoLocal['parametros']): Record<string, unknown> {
  const nome = prazoVigiadoDe(p);
  return nome ? { prazoId: `{{${nome}}}` } : {};
}

function variavelDoPrazo(p: BlocoLocal['parametros']): Record<string, unknown> {
  const nome = variavelGravadaPeloPrazo(p);
  return nome ? { savePrazoIdToVariable: nome } : {};
}

/**
 * O core só lê o `pendenciaId` com "Pendência resolvida" ou "Pendência expirada" na lista
 * (core#2249); fora disso o campo não vai, e o `node.data` sai como o de uma espera sem vínculo.
 */
function pendenciaIdDaEspera(p: BlocoLocal['parametros']): Record<string, unknown> {
  const valor = pendenciaVigiadaDe(p);
  return valor ? { pendenciaId: pendenciaIdDoNode(valor) } : {};
}

function variavelDaPendencia(p: BlocoLocal['parametros']): Record<string, unknown> {
  const nome = variavelGravadaPelaPendencia(p);
  return nome ? { savePendenciaIdToVariable: nome } : {};
}

function construirDataNode(bloco: BlocoLocal): Record<string, unknown> {
  const p = bloco.parametros;
  switch (bloco.tipo) {
    case 'CONDICAO':
      return construirDataCondicao(p);
    case 'MUDAR_STATUS':
      return { novoStatus: p.statusDestino };
    case 'SET_SITUACAO':
      // Contrato do executor arbitral.set_situacao: resolve por `key` (escopo do
      // escritório > global). `desativarAnteriores` limpa as situações ativas antes.
      return {
        situacaoKeys: Array.isArray(p.situacaoKeys) ? p.situacaoKeys : [],
        desativarAnteriores: Boolean(p.desativarAnteriores),
      };
    case 'CRIAR_ANDAMENTO': {
      // Contrato do executor arbitral.criar_andamento (Tela 06): a UI usa `texto`,
      // o executor lê `descricao`. `modeloId` presente → modoTexto MODELO (apenas
      // proveniência; o texto já foi materializado em `texto`). Sem `visibilidade`: não
      // existe andamento interno (C21), o core ignora o campo. `modeloId` cru
      // não vai (não é campo de texto), só como rastreio quando há modelo.
      //
      // O "Tipo de andamento" da UI é a classificação de 6 valores dos MODELOS
      // (TipoModeloAndamento: INFORMATIVO/SISTEMA/DESPACHO/...) — vai em
      // `categoriaModelo`. O `tipo` do executor é o enum TipoAndamento
      // (tipoStatus), onde andamento de fluxo é sempre MANUAL — mandar a
      // categoria como `tipo` quebrava o INSERT em runtime.
      const modeloId = (p.modeloId as string) || undefined;
      const categoriaModelo = (p.tipo as string) || undefined;
      return {
        tipo: 'MANUAL',
        descricao: p.texto,
        modoTexto: modeloId ? 'MODELO' : 'LIVRE',
        ...(categoriaModelo ? { categoriaModelo } : {}),
        ...(modeloId ? { modeloId } : {}),
      };
    }
    case 'SORTEAR_ARBITRO':
      return {
        algoritmo: p.algoritmo,
        excluirArbitroIds: Array.isArray(p.excluirArbitroIds) ? p.excluirArbitroIds : [],
      };
    case 'AGUARDAR_EVENTO':
      return {
        eventTypes: Array.isArray(p.eventos) ? p.eventos : [],
        respectBusinessHours: true,
        ...pendenciaIdDaEspera(p),
        ...prazoIdDaEspera(p),
      };
    case 'AGUARDAR_VISUALIZACAO':
      // `bastaUma` libera a espera na primeira leitura — a da intimação da sentença a todas as
      // partes. Sem campo na tela: vem da migration `20261020180000` do core, e descartá-lo aqui
      // faria a próxima publicação voltar a exigir a leitura de todas as partes.
      return { respectBusinessHours: true, ...(p.bastaUma === true ? { bastaUma: true } : {}) };
    case 'AGUARDAR_CITACAO':
      return { respectBusinessHours: true };
    case 'AGUARDAR_EMBARGOS':
      return { prazoDiasUteis: p.diasUteis };
    case 'INICIAR_PRAZO': {
      // O executor trata `pausar` ausente como modo pausa: a execução fica WAITING até o
      // vencimento e descarta todo evento que chega no meio (ARB-202609-000012). Só o boolean
      // explícito vai; injetar `false` onde não havia mudaria fluxos que dependem da pausa.
      // `nomePrazo` só vale sem `tipoPrazoId` (com tipo, o executor usa o nome do tipo).
      const nomePrazo = typeof p.nomePrazo === 'string' ? p.nomePrazo.trim() : '';
      return {
        tipoPrazoId: p.tipoPrazoId ?? null,
        diasUteis: p.diasUteis,
        ...(typeof p.pausar === 'boolean' ? { pausar: p.pausar } : {}),
        ...(nomePrazo ? { nomePrazo } : {}),
        ...variavelDoPrazo(p),
      };
    }
    case 'ENVIAR_EMAIL':
    case 'ENVIAR_WHATSAPP':
      return { para: p.para, modeloComunicacaoId: p.modeloComunicacaoId };
    case 'NOTIF_INAPP':
      return { para: p.para, modeloNotificacaoId: p.modeloNotificacaoId };
    case 'ENVIAR_AR':
      return {
        modeloCartaId: p.modeloCartaId,
        somenteSeEmailNaoAberto: p.somenteSeEmailNaoAberto !== false,
        prazoDiasUteis: p.prazoDiasUteis,
      };
    case 'INTIMAR_SENTENCA': {
      const assunto = typeof p.assuntoEmail === 'string' ? p.assuntoEmail.trim() : '';
      return {
        ...compilarAlvoDaIntimacao(p),
        ...(p.templateId
          ? { templateId: p.templateId }
          : p.templateVersaoId
            ? { templateVersaoId: p.templateVersaoId }
            : {}),
        ...(assunto ? { assuntoEmail: assunto } : {}),
        ...(p.modeloEmailId ? { modeloEmailId: p.modeloEmailId } : {}),
        ...(p.lexiFlowId ? { lexiFlowId: p.lexiFlowId } : {}),
        gerarDocumentoPdf: p.gerarDocumentoPdf !== false,
      };
    }
    case 'CITACAO':
      // `janelaReversaoMinutos` só viaja quando o autor mexeu nele: o executor
      // aplica o mesmo default quando o campo não vem (fluxos já publicados).
      return {
        ...(p.fluxoCitacaoId ? { fluxoCitacaoId: p.fluxoCitacaoId } : {}),
        ...compilarEmailsCitacao(lerEmailsCitacao(p)),
        ...(typeof p.janelaReversaoMinutos === 'number'
          ? { janelaReversaoMinutos: p.janelaReversaoMinutos }
          : {}),
      };
    case 'PUBLICAR_DECURSO':
      return {
        ...(p.templateId ? { templateId: p.templateId } : {}),
      };
    case 'PUBLICAR_TRANSITO':
      // Contrato do executor arbitral.publicar_transito (Tela 10): motivo do
      // encerramento (fallback SENTENCA_ARBITRAL espelha o default do backend).
      // `lexiFlowId` ausente = Lexi padrão do escritório (A-13), como no INTIMAR_SENTENCA.
      return {
        motivoEncerramento: p.motivoEncerramento ?? 'SENTENCA_ARBITRAL',
        ...(p.templateId ? { templateId: p.templateId } : {}),
        ...(p.lexiFlowId ? { lexiFlowId: p.lexiFlowId } : {}),
      };
    case 'FINALIZAR_PROCESSO':
      // Contrato do executor arbitral.finalizar_processo (Tela 12): status
      // terminal + motivo (texto só quando OUTROS) + fechar pendências.
      return {
        statusFinal: p.statusFinal,
        motivoEncerramento: p.motivoEncerramento,
        motivoEncerramentoTexto:
          p.motivoEncerramento === 'OUTROS' ? (p.motivoEncerramentoTexto as string) : undefined,
        fecharPendencias: p.fecharPendencias !== false,
      };
    case 'EXIGIR_TERMO_IMPARCIALIDADE':
      // Sem `prazoDiasUteis`: o core lê o prazo do termo do cadastro "Aceite Nomeação e Termo
      // (Árbitro)" (core#2456). O do bloco salvo antes disso fica inerte nos parâmetros.
      return {
        ...(p.templateIdTermoImparcialidade
          ? { templateIdTermoImparcialidade: p.templateIdTermoImparcialidade }
          : {}),
        ...(p.templateIdTermoArbitragem
          ? { templateIdTermoArbitragem: p.templateIdTermoArbitragem }
          : {}),
      };
    case 'CONCLUSO':
      // Contrato do executor arbitral.concluso (`ArbitralConclusoNodeData`): o texto vai como
      // `descricao`, com as variáveis cruas — o executor as resolve com o processo.
      return { descricao: p.descricao, prazoDiasUteis: p.prazoDiasUteis };
    case 'GERAR_DOCUMENTO':
      // Contrato do executor arbitral.gerar_certidao (Tela 08): `categoria`
      // (gera a versão ativa), não `modeloId`. visivelPara (single-select na UI)
      // vai como array; publicação/visibilidade persistem no snapshot.
      return {
        categoria: p.categoria,
        ...(p.templateId ? { templateId: p.templateId } : {}),
        publicarAutomatico: Boolean(p.publicarAutomatico),
        visivelPara: p.visivelPara ? [p.visivelPara] : ['TODOS'],
      };
    case 'COBRAR_ASAAS':
      // Contrato do executor arbitral.cobrar_pagamento (Fase 2 avulso): a UI só
      // captura o vencimento em DU; o meio de pagamento é escolhido pelo
      // requerente no checkout do Asaas (billingType UNDEFINED) e o valor vem
      // do Product ARBITRAGEM no backend (override `valor` existe no executor,
      // sem UI). Conveniado (PER_BATCH) é no-op no próprio executor.
      return {
        vencimentoDiasUteis: p.vencimentoDiasUteis ?? 7,
      };
    case 'REGISTRAR_CONSUMO_CONVENIO':
      // Contrato do executor arbitral.registrar_consumo_convenio: sem parâmetros
      // de UI. O executor deriva empresa/escritório/modalidade do próprio
      // processo e delega ao ArbitralConvenioService (no-op para avulso).
      return {};
    case 'CRIAR_PENDENCIA':
      // Contrato do executor arbitral.criar_pendencia (Tela 07): tipo+titulo
      // obrigatórios; responsavelTipo (UI) → solicitante (enum back); prazo em
      // dias úteis calculado no backend. `requerValidacao` governa o ciclo de
      // vida da resposta (secretaria valida vs. resolve direto — #9).
      // `destinatario` = "quem responde" (enum DestinatarioPendenciaArbitral;
      // vazio → omitido = pendência sem destinatário, comportamento vigente).
      // `bloqueiaAvanco` pausa a execução até PENDENCIA_RESOLVIDA (T07+T13-D);
      // default true no executor — só vai false quando o autor desligar.
      return {
        tipo: p.tipo,
        titulo: p.titulo,
        descricao: (p.descricao as string) || undefined,
        solicitante: p.responsavelTipo,
        destinatario: (p.destinatario as string) || undefined,
        prazoDiasUteis: p.prazoDiasUteis,
        requerValidacao: Boolean(p.requerValidacao),
        bloqueiaAvanco: p.bloqueiaAvanco !== false,
        ...variavelDaPendencia(p),
      };
    default:
      return { ...p };
  }
}

function comEtapa(ctx: CompilerCtx, data: Record<string, unknown>): Record<string, unknown> {
  // `etapaId` é metadado de autoria (tolerado pelo `.passthrough()` do backend),
  // não parâmetro de execução — por isso fica fora de `construirDataNode`.
  if (!ctx.etapaAtual) return data;
  return {
    ...data,
    etapaId: ctx.etapaAtual.id,
    etapaNome: ctx.etapaAtual.nome,
    etapaOrdem: ctx.etapaAtual.ordem,
  };
}

function addNode(ctx: CompilerCtx, bloco: BlocoComNo, data: Record<string, unknown>): void {
  ctx.nodes.push({
    id: bloco.id,
    type: NODE_TYPE_POR_TIPO[bloco.tipo],
    position: { x: 100, y: ctx.posY.value },
    data: comEtapa(ctx, data),
  });
  ctx.posY.value += 120;
}

function addConnection(
  ctx: CompilerCtx,
  sourceNodeId: string,
  targetNodeId: string,
  sourceHandle?: string,
): void {
  const sufixo = sourceHandle ? `:${sourceHandle}` : '';
  ctx.connections.push({
    id: `${sourceNodeId}->${targetNodeId}${sufixo}`,
    sourceNodeId,
    targetNodeId,
    sourceHandle,
  });
}

// TODO(#904): avisar em `avisosFluxo` quando houver bloco/etapa depois de um bloco terminal.
const TIPOS_BLOCO_TERMINAIS: ReadonlySet<TipoBlocoComNo> = new Set<TipoBlocoComNo>([
  'FINALIZAR_PROCESSO',
]);

const NODE_TYPES_TERMINAIS: ReadonlySet<string> = new Set(
  [...TIPOS_BLOCO_TERMINAIS].map((tipo) => NODE_TYPE_POR_TIPO[tipo]),
);

function resolverSaidaSequencial(tipo: TipoBlocoComNo, proximo: string | null): string | null {
  if (TIPOS_BLOCO_TERMINAIS.has(tipo)) return null;
  return proximo;
}

/**
 * Compila uma sequência (etapa ou ramo) e devolve a entrada dela.
 *
 * O "Ir para" não vira nó: corta a sequência e o destino passa a ser a continuação do trecho
 * anterior — então um ramo só com "Ir para" tem o destino como entrada, e o `node.data` de
 * ninguém muda, só os alvos (ligação, `eventRouting`, `timeoutNodeId`). O que vier depois do
 * salto (a validação acusa) segue no grafo com a continuação natural, alcançável só por outro
 * salto.
 */
function compilarSequencia(
  blocos: BlocoLocal[],
  continuacao: string | null,
  ctx: CompilerCtx,
): string | null {
  const corte = blocos.findIndex((b) => !temNo(b));
  if (corte === -1) return compilarTrecho(blocos as BlocoComNo[], continuacao, ctx);

  const entrada = compilarTrecho(
    blocos.slice(0, corte) as BlocoComNo[],
    alvoDoSalto(blocos[corte], ctx) ?? continuacao,
    ctx,
  );
  compilarSequencia(blocos.slice(corte + 1), continuacao, ctx);
  return entrada;
}

function compilarTrecho(
  blocos: BlocoComNo[],
  continuacao: string | null,
  ctx: CompilerCtx,
): string | null {
  if (blocos.length === 0) return continuacao;
  let entryId: string | null = null;
  for (let i = 0; i < blocos.length; i++) {
    const proximo = i < blocos.length - 1 ? blocos[i + 1].id : continuacao;
    const id = compilarBloco(blocos[i], proximo, ctx);
    if (i === 0) entryId = id;
  }
  return entryId;
}

const RAMO_SUCESSO_DA_ESPERA = {
  AGUARDAR_CITACAO: 'aoCitar',
  AGUARDAR_VISUALIZACAO: 'aoVisualizar',
} as const;

/**
 * Vencimento das três esperas. No modo SEGUIR_RAMO (ou sem modo) o `node.data` sai igual ao de
 * antes do modo existir: `timeoutNodeId` + `timeoutDiasUteis` só quando há ramo para onde ir.
 * Em AVISAR_E_AGUARDAR o core retoma no próprio nó ao vencer, então não há `timeoutNodeId` nem
 * ramo compilado, e o prazo vai mesmo quando a espera é o último bloco.
 */
function aplicarVencimentoDaEspera(
  bloco: BlocoComNo,
  data: Record<string, unknown>,
  proximo: string | null,
  ctx: CompilerCtx,
): void {
  const timeoutDiasUteis = bloco.parametros.timeoutDiasUteis;
  const temPrazo = typeof timeoutDiasUteis === 'number' && Number.isFinite(timeoutDiasUteis);

  if (avisaEAguarda(bloco)) {
    data.modoVencimento = 'AVISAR_E_AGUARDAR';
    data.avisoAtraso = lerAvisoAtraso(bloco.parametros.avisoAtraso);
    if (temPrazo) data.timeoutDiasUteis = timeoutDiasUteis;
    return;
  }

  const timeoutEntry = compilarSequencia(lerSubBlocos(bloco, 'aoTimeout'), proximo, ctx);
  if (timeoutEntry && temPrazo) {
    data.timeoutNodeId = timeoutEntry;
    data.timeoutDiasUteis = timeoutDiasUteis;
  }
}

function compilarBloco(bloco: BlocoComNo, proximo: string | null, ctx: CompilerCtx): string {
  if (bloco.tipo === 'CONDICAO') {
    addNode(ctx, bloco, construirDataCondicao(bloco.parametros));
    const trueEntry = compilarSequencia(lerSubBlocos(bloco, 'seVerdadeiro'), proximo, ctx);
    const falseEntry = compilarSequencia(lerSubBlocos(bloco, 'seFalso'), proximo, ctx);
    if (trueEntry) addConnection(ctx, bloco.id, trueEntry, 'true');
    if (falseEntry) addConnection(ctx, bloco.id, falseEntry, 'false');
    return bloco.id;
  }

  if (bloco.tipo === 'AGUARDAR_EVENTO') {
    const porEvento = bloco.parametros.porEvento === true;
    const eventos = Array.isArray(bloco.parametros.eventos)
      ? (bloco.parametros.eventos as string[])
      : [];
    const data = construirDataNode(bloco);
    aplicarVencimentoDaEspera(bloco, data, proximo, ctx);

    if (porEvento) {
      // Decisão por evento: cada evento compila o próprio ramo e o executor
      // retoma pelo `eventRouting[eventType]` (fallback: conexão default).
      const eventRouting: Record<string, string> = {};
      for (const evento of eventos) {
        const entry = compilarSequencia(
          blocosDoRamo(bloco, { ramo: 'aoOcorrerPorEvento', evento }),
          proximo,
          ctx,
        );
        if (entry) eventRouting[evento] = entry;
      }
      if (Object.keys(eventRouting).length > 0) data.eventRouting = eventRouting;
      addNode(ctx, bloco, data);
      if (proximo) addConnection(ctx, bloco.id, proximo);
      return bloco.id;
    }

    const ocorrerEntry = compilarSequencia(lerSubBlocos(bloco, 'aoOcorrer'), proximo, ctx);
    addNode(ctx, bloco, data);
    if (ocorrerEntry) addConnection(ctx, bloco.id, ocorrerEntry);
    return bloco.id;
  }

  // Sem este ramo os sub-blocos de sucesso/`aoTimeout` nunca seriam compilados — o canvas
  // mostraria as ações e o fluxo publicado não as teria, sem nada acusando (o caminho
  // sequencial abaixo não erra, só ignora).
  if (bloco.tipo === 'AGUARDAR_CITACAO' || bloco.tipo === 'AGUARDAR_VISUALIZACAO') {
    const sucessoEntry = compilarSequencia(
      lerSubBlocos(bloco, RAMO_SUCESSO_DA_ESPERA[bloco.tipo]),
      proximo,
      ctx,
    );
    const data = construirDataNode(bloco);
    aplicarVencimentoDaEspera(bloco, data, proximo, ctx);
    addNode(ctx, bloco, data);
    if (sucessoEntry) addConnection(ctx, bloco.id, sucessoEntry);
    return bloco.id;
  }

  addNode(ctx, bloco, construirDataNode(bloco));
  const saida = resolverSaidaSequencial(bloco.tipo, proximo);
  if (saida) addConnection(ctx, bloco.id, saida);
  return bloco.id;
}

function blocosOrdenados(etapa: EtapaLocal): BlocoLocal[] {
  return [...etapa.blocos].sort((a, b) => a.ordem - b.ordem);
}

const SUFIXO_GATE_ETAPA = '__gate';

/**
 * Entrada "própria" da etapa (sem resolver para a próxima): gate, 1º bloco, ou nada. Etapa que
 * abre com "Ir para" entra direto no destino (ou em nada, se o salto for inválido — e aí a
 * etapa anterior emenda na seguinte, como o `compilarSequencia` faz).
 */
function entradaLocalDaEtapa(etapa: EtapaLocal, ctx: CompilerCtx): string | null {
  if (etapa.condicaoEntrada) return `${etapa.id}${SUFIXO_GATE_ETAPA}`;
  const primeiro = blocosOrdenados(etapa)[0];
  if (!primeiro) return null;
  return temNo(primeiro) ? primeiro.id : alvoDoSalto(primeiro, ctx);
}

function resolverFalsoTarget(
  aoFalsoIrPara: string | null | undefined,
  proximaEntrada: string | null,
  entradaPorEtapaId: Map<string, string | null>,
): string | null {
  if (aoFalsoIrPara === 'FIM') return null;
  if (!aoFalsoIrPara) return proximaEntrada;
  return entradaPorEtapaId.get(aoFalsoIrPara) ?? proximaEntrada;
}

function compilarEtapa(
  etapa: EtapaLocal,
  proximaEntrada: string | null,
  ctx: CompilerCtx,
  entradaPorEtapaId: Map<string, string | null>,
): void {
  const blocos = blocosOrdenados(etapa);
  if (!etapa.condicaoEntrada) {
    compilarSequencia(blocos, proximaEntrada, ctx);
    return;
  }
  // Etapa condicional (Tela 18): nó `condition` na entrada; true → 1º bloco da
  // etapa (que encadeia até a próxima), false → salto sem reconverter.
  const blocosEntry = compilarSequencia(blocos, proximaEntrada, ctx);
  const gateId = `${etapa.id}${SUFIXO_GATE_ETAPA}`;
  ctx.nodes.push({
    id: gateId,
    type: 'condition',
    position: { x: 100, y: ctx.posY.value },
    data: comEtapa(
      ctx,
      construirDataCondicao(etapa.condicaoEntrada as unknown as Record<string, unknown>),
    ),
  });
  ctx.posY.value += 120;
  const trueTarget = blocosEntry ?? proximaEntrada;
  const falseTarget = resolverFalsoTarget(
    etapa.condicaoEntrada.aoFalsoIrPara,
    proximaEntrada,
    entradaPorEtapaId,
  );
  if (trueTarget) addConnection(ctx, gateId, trueTarget, 'true');
  if (falseTarget) addConnection(ctx, gateId, falseTarget, 'false');
}

interface EtapasCompiladas {
  nodes: RawNode[];
  connections: RawConnection[];
  startNodeId: string | null;
  etapasMetadata: RawMetadataEtapa[];
}

/**
 * A guarda do editor (`motivoSomenteEditorDeNos`) compara o grafo e o `node.data` salvos com o
 * que esta função emite hoje. Mudar uma ligação emitida trava todo fluxo gravado antes, então a
 * mudança vem junto com o tratamento do grafo antigo em `ligacoesDoGrafo`. Mudar o valor emitido
 * para uma chave que o fluxo salvo já traz (um default, um formato) também trava, então a
 * mudança vem com a migração registrada em `CHAVES_QUE_O_SAVE_MIGRA`. Um default emitido que
 * difere do que o core faz com a chave ausente entra em `DEFAULT_DO_CORE_QUANDO_AUSENTE`, senão
 * o fluxo salvo sem a chave muda de comportamento no save sem a guarda ver. O fixture congelado
 * `__fixtures__/fluxoSalvoPelaRegua.json` reprova quando isso falta.
 */
function compilarEtapas(etapas: EtapaLocal[]): EtapasCompiladas {
  return compilarEtapasCom(etapas, idsDosBlocosComNo(etapas));
}

/**
 * Os blocos que o compilador emite como nó — os destinos válidos de um "Ir para". Quais blocos
 * viram nó não depende de para onde os saltos vão (só as ligações dependem), então uma passada
 * sem saltos responde, e um bloco num ramo que o compilador não emite fica de fora sozinho.
 */
export function idsDosBlocosComNo(etapas: EtapaLocal[]): ReadonlySet<string> {
  return new Set(compilarEtapasCom(etapas, new Set()).nodes.map((n) => n.id));
}

/** O grafo como o motor o percorre, com cada "Ir para" válido como ponto de passagem. */
interface GrafoDeExecucao {
  /** Ids dos nós que o fluxo publica (blocos e gates de etapa). */
  nos: string[];
  /** `salto.id → destino` de cada "Ir para" que emenda num nó. */
  saltos: ReadonlyMap<string, string>;
  /** Origem → destinos, com os saltos já intercalados (`origem → salto.id → destino`). */
  sucessores: ReadonlyMap<string, readonly string[]>;
}

export function grafoDeExecucao(etapas: EtapaLocal[]): GrafoDeExecucao {
  const saltos = new Map<string, string>();
  const { nodes, connections } = compilarEtapasCom(etapas, idsDosBlocosComNo(etapas), saltos);
  const sucessores = new Map<string, string[]>();
  const ligar = (origem: string, destino: string) => {
    const lista = sucessores.get(origem);
    if (lista) lista.push(destino);
    else sucessores.set(origem, [destino]);
  };
  for (const { origem, destino } of arestasDoGrafo(nodes, connections)) ligar(origem, destino);
  for (const [salto, destino] of saltos) ligar(salto, destino);
  return { nos: nodes.map((no) => no.id), saltos, sucessores };
}

function compilarEtapasCom(
  etapas: EtapaLocal[],
  idsComNo: ReadonlySet<string>,
  saltos?: Map<string, string>,
): EtapasCompiladas {
  const ctx: CompilerCtx = { nodes: [], connections: [], posY: { value: 0 }, idsComNo, saltos };

  const etapasOrdenadas = [...etapas].sort((a, b) => a.ordem - b.ordem);
  const n = etapasOrdenadas.length;

  // Entrada resolvida de cada etapa (pula etapas vazias), do fim para o início.
  const entradaResolvida: (string | null)[] = new Array(n).fill(null);
  for (let i = n - 1; i >= 0; i--) {
    const proxima = i < n - 1 ? entradaResolvida[i + 1] : null;
    entradaResolvida[i] = entradaLocalDaEtapa(etapasOrdenadas[i], ctx) ?? proxima;
  }
  const entradaPorEtapaId = new Map<string, string | null>();
  etapasOrdenadas.forEach((e, i) => entradaPorEtapaId.set(e.id, entradaResolvida[i]));

  for (let i = 0; i < n; i++) {
    const etapa = etapasOrdenadas[i];
    ctx.etapaAtual = { id: etapa.id, nome: etapa.nome, ordem: etapa.ordem };
    compilarEtapa(etapa, i < n - 1 ? entradaResolvida[i + 1] : null, ctx, entradaPorEtapaId);
  }
  ctx.etapaAtual = undefined;

  return {
    nodes: ctx.nodes,
    connections: ctx.connections,
    startNodeId: entradaResolvida[0] ?? null,
    etapasMetadata: etapasOrdenadas.map((e) => ({
      id: e.id,
      nome: e.nome,
      ordem: e.ordem,
      statusEntrada: e.statusEntrada,
      statusSaida: e.statusSaida,
    })),
  };
}

export function toFlowDefinitionPatch(fluxo: FluxoArbitralEditavel): FlowDefinitionPatch {
  const { nodes, connections, startNodeId, etapasMetadata } = compilarEtapas(fluxo.etapas);

  const metadata: Record<string, unknown> = {
    rito: fluxo.rito,
    etapas: etapasMetadata,
    editorSource: { etapas: fluxo.etapas },
    editorVersion: 2,
    modoAutoria: fluxo.modoAutoria,
    ...(fluxo.gatilho ? { gatilho: fluxo.gatilho } : {}),
  };

  return {
    definition: {
      nodes,
      connections,
      variables: [],
      metadata,
    },
    metadata,
    startNodeId,
  };
}

export function novaEtapaLocal(input: {
  nome: string;
  ordem: number;
  statusEntrada: ProcessoArbitralStatus | null;
  statusSaida: ProcessoArbitralStatus | null;
}): EtapaLocal {
  return {
    id: gerarId('etapa'),
    nome: input.nome,
    ordem: input.ordem,
    statusEntrada: input.statusEntrada,
    statusSaida: input.statusSaida,
    blocos: [],
  };
}

export function novoBlocoLocal(input: {
  tipo: TipoBloco;
  ordem: number;
  label?: string;
  parametrosPadrao: Record<string, unknown>;
}): BlocoLocal {
  return {
    id: gerarId('bloco'),
    tipo: input.tipo,
    label: input.label,
    ordem: input.ordem,
    parametros: { ...input.parametrosPadrao },
  };
}

function pareceBlocoLocal(value: unknown): value is BlocoLocal {
  if (!value || typeof value !== 'object') return false;
  const candidato = value as Record<string, unknown>;
  return (
    typeof candidato.id === 'string' &&
    typeof candidato.tipo === 'string' &&
    typeof candidato.parametros === 'object' &&
    candidato.parametros !== null
  );
}

function pareceListaDeBlocos(valor: unknown): valor is BlocoLocal[] {
  return Array.isArray(valor) && valor.length > 0 && valor.every(pareceBlocoLocal);
}

function pareceRecordDeListasDeBlocos(valor: unknown): valor is Record<string, BlocoLocal[]> {
  if (!valor || typeof valor !== 'object' || Array.isArray(valor)) return false;
  const listas = Object.values(valor as Record<string, unknown>);
  return listas.length > 0 && listas.every((lista) => Array.isArray(lista));
}

/** Id original → id da cópia, de tudo o que uma mesma clonagem copiou. */
type IdsDaCopia = Map<string, string>;

function clonarParametros(
  parametros: Record<string, unknown>,
  ids: IdsDaCopia,
): Record<string, unknown> {
  const clonados: Record<string, unknown> = {};
  for (const [chave, valor] of Object.entries(parametros)) {
    if (pareceListaDeBlocos(valor)) {
      clonados[chave] = valor.map((sub) => clonarComIds(sub, ids));
    } else if (chave === 'aoOcorrerPorEvento' && pareceRecordDeListasDeBlocos(valor)) {
      clonados[chave] = Object.fromEntries(
        Object.entries(valor).map(([evento, lista]) => [
          evento,
          lista.map((sub) => clonarComIds(sub, ids)),
        ]),
      );
    } else {
      clonados[chave] = structuredClone(valor);
    }
  }
  return clonados;
}

function clonarComIds(bloco: BlocoLocal, ids: IdsDaCopia): BlocoLocal {
  const id = gerarId('bloco');
  ids.set(bloco.id, id);
  return { ...bloco, id, parametros: clonarParametros(bloco.parametros, ids) };
}

/**
 * D-05: o "Ir para" copiado junto com o destino aponta para a cópia do destino; o que aponta
 * para fora do que foi copiado mantém o destino. Roda depois da cópia inteira porque o destino
 * pode vir depois do salto. Só toca objetos recém-criados pela clonagem.
 */
function apontarSaltosParaAsCopias(blocos: BlocoLocal[], ids: IdsDaCopia): void {
  for (const bloco of blocos) {
    const destino = bloco.parametros.destinoBlocoId;
    if (bloco.tipo === 'IR_PARA' && typeof destino === 'string' && ids.has(destino)) {
      bloco.parametros.destinoBlocoId = ids.get(destino);
    }
    apontarSaltosParaAsCopias(subBlocosClonaveis(bloco.parametros), ids);
  }
}

/** Os sub-blocos que `clonarParametros` reconhece e copia. */
function subBlocosClonaveis(parametros: Record<string, unknown>): BlocoLocal[] {
  return Object.entries(parametros).flatMap(([chave, valor]) => {
    if (pareceListaDeBlocos(valor)) return valor;
    if (chave === 'aoOcorrerPorEvento' && pareceRecordDeListasDeBlocos(valor)) {
      return Object.values(valor).flat();
    }
    return [];
  });
}

export function clonarBlocoLocal(bloco: BlocoLocal): BlocoLocal {
  const ids: IdsDaCopia = new Map();
  const copia = clonarComIds(bloco, ids);
  apontarSaltosParaAsCopias([copia], ids);
  return copia;
}

export function clonarEtapaLocal(etapa: EtapaLocal): EtapaLocal {
  const ids: IdsDaCopia = new Map();
  const blocos = etapa.blocos.map((bloco) => clonarComIds(bloco, ids));
  apontarSaltosParaAsCopias(blocos, ids);
  return {
    ...etapa,
    id: gerarId('etapa'),
    condicaoEntrada: etapa.condicaoEntrada
      ? structuredClone(etapa.condicaoEntrada)
      : etapa.condicaoEntrada,
    blocos,
  };
}

function gerarId(prefix: string): string {
  const cryptoApi =
    typeof globalThis !== 'undefined' &&
    'crypto' in globalThis &&
    typeof globalThis.crypto?.randomUUID === 'function'
      ? globalThis.crypto
      : null;
  if (cryptoApi) return `${prefix}-${cryptoApi.randomUUID()}`;
  return `${prefix}-${Math.random().toString(36).slice(2, 11)}${Date.now().toString(36)}`;
}
